import {RelatedCardType} from 'common/src/modules/excelToCampaign/excelToCampaign.enums';
import {CampaignSignal} from 'common/src/modules/excelToCampaign/excelToCampaign.types';
import {ColumnToSignalMapping} from '../../excelImport.types';
import {MappingValidationError} from '../validateMappingsTool.types';
import _groupBy from 'lodash/groupBy';
import _mapValues from 'lodash/mapValues';
import _sortBy from 'lodash/sortBy';

function validateSegmentationMapping({
  allSignals,
  mappings,
}: {
  allSignals: CampaignSignal[];
  mappings: ColumnToSignalMapping[];
}): MappingValidationError[] {
  const issues: MappingValidationError[] = [];

  // Create a Set of all signal keys that are related to a condition card.
  const conditionSignalKeys = new Set(
    allSignals
      .filter((signal) => signal.relatedCardType === RelatedCardType.Condition)
      .map((signal) => signal.signalKey),
  );

  // get all segmentation mappings (mappings that are related to a condition card).
  const segmentationMappings = mappings.filter((mapping) =>
    conditionSignalKeys.has(mapping.signalKey),
  );

  // if no segmentation mappings - nothing to check.
  if (segmentationMappings.length === 0) return issues;

  // there should be no more than one segmentation mapping.
  if (segmentationMappings.length > 1) {
    const message = `More than one segmentation column was found: ${segmentationMappings
      .map((mapping) => mapping.header)
      .join(', ')}. Please choose only one segmentation column from the list.`;

    issues.push({messageToUser: message, instructionsForAgent: ''});
  }

  // make sure that the segmentation column is mapped to a campaign signal that has value examples.
  // for example - if the "Audience(s)" was mapped to "placement.custom1" and "placement.custom1" does not have value examples, this a validation error.
  const [segmentationMapping] = segmentationMappings;
  if (segmentationMapping.isFixedByUser) return issues;

  const mappedSignal = allSignals.find(
    (signal) => signal.signalKey === segmentationMapping.signalKey,
  );

  if (mappedSignal?.valueExamples.length === 0) {
    const error = _buildNoValueExamplesForSegmentationMappingError({
      allSignals,
      mappings,
      currentMapping: segmentationMapping,
    });

    issues.push(error);
  }

  return issues;
}

function _buildNoValueExamplesForSegmentationMappingError({
  allSignals,
  mappings,
  currentMapping,
}: {
  allSignals: CampaignSignal[];
  mappings: ColumnToSignalMapping[];
  currentMapping: ColumnToSignalMapping;
}): MappingValidationError {
  const message = `I was unable to map the '${currentMapping.header.name}' column to a campaign element.
Please choose a data source from the list below.
Your choice will be used to populate the condition card that represents the '${currentMapping.header}' column.`;

  // Create a Set of all mapped signal keys (excluding the current one, because it can be reused)
  const mappedSignalKeys = new Set(
    mappings
      .map((mapping) => mapping.signalKey)
      .filter((signalKey) => signalKey !== currentMapping.signalKey),
  );

  // Filter signals that are related to a condition card
  // AND are the same type as the column values type
  // AND are not already mapped
  const filteredSignals = allSignals.filter(
    (signal) =>
      signal.relatedCardType === RelatedCardType.Condition &&
      signal.signalTypeName === currentMapping.columnValuesType &&
      !mappedSignalKeys.has(signal.signalKey),
  );

  // Group by the prefix before the dash
  const groupedSignals = _groupBy(filteredSignals, (item) => {
    return item.name.split(' - ')[0].trim();
  });

  // Sort each group alphabetically by the full name
  const sortedGrouped = _mapValues(groupedSignals, (group) => {
    return _sortBy(group, (item) => item.name.toLowerCase());
  });

  return {
    messageToUser: message,
    instructionsForAgent: `These are the user's options, grouped by data source:

    ${JSON.stringify(sortedGrouped, null, 2)}

1. First ask the user to choose a data source, and add a last option to the list: "I'm not sure yet".
2. Only after the user chooses a data source, let the user choose from the campaign elements in the chosen data source.
3. If the user chooses "I'm not sure yet", map the column to null`,
  };
}

export default validateSegmentationMapping;
